import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Menu, X, Phone } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
  { to: "/", label: "Accueil", id: "nav-home" },
  { to: "/la-maladie", label: "La maladie", id: "nav-maladie" },
  { to: "/vivre-avec", label: "Vivre avec", id: "nav-vivre-avec" },
  { to: "/suivi-medical", label: "Suivi médical", id: "nav-suivi-medical" },
  { to: "/nos-actions", label: "Nos actions", id: "nav-actions" },
  { to: "/livre-espoir", label: "Livre d'espoir", id: "nav-livre-espoir" },
  { to: "/actualites", label: "Actualités", id: "nav-actualites" },
  { to: "/contact", label: "Contact", id: "nav-contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
        scrolled || open
          ? "bg-white/90 backdrop-blur-xl border-slate-200/80 shadow-sm"
          : "bg-white/60 backdrop-blur-md border-transparent"
      }`}
    >
      <a
        href="#contenu"
        data-testid="skip-to-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-white focus:px-4 focus:py-2 focus:rounded-lg focus:font-bold focus:text-[#0F2C59]"
      >
        Aller au contenu
      </a>
      <div className="container-site flex items-center justify-between gap-3 py-3">
        <Link to="/" aria-label="Aide au Monde et Santé - Accueil" data-testid="nav-logo">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Navigation principale">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `rounded-full px-3.5 py-2 text-[13.5px] font-semibold transition-[background-color,color] duration-200 ${
                  isActive
                    ? "bg-[#EFF6FF] text-[#0F2C59]"
                    : "text-slate-600 hover:text-[#0F2C59] hover:bg-slate-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="tel:+33954243446"
            data-testid="header-call-button"
            className="hidden xl:inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-bold text-[#0F2C59] hover:bg-slate-100 transition-colors"
            aria-label="Appeler l'association au 09 54 24 34 46"
          >
            <Phone size={16} strokeWidth={2.5} />
            09 54 24 34 46
          </a>
          <Link
            to="/faire-un-don"
            data-testid="nav-donate-button"
            className="btn-don don-pulse !px-5 !py-2.5 !text-sm"
          >
            <Heart size={16} strokeWidth={2.5} fill="currentColor" />
            Faire un don
          </Link>
          <button
            type="button"
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            className="lg:hidden inline-flex items-center justify-center rounded-full p-2.5 text-[#0F2C59] hover:bg-slate-100 transition-colors"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden border-t border-slate-200/70 bg-white/95 backdrop-blur-xl"
            aria-label="Navigation mobile"
            data-testid="mobile-menu"
          >
            <div className="container-site py-4 flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.3 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.id}`}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-3 text-base font-semibold transition-colors ${
                        isActive ? "bg-[#EFF6FF] text-[#0F2C59]" : "text-slate-700 hover:bg-slate-100"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <a
                href="tel:+33954243446"
                data-testid="mobile-call-button"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[#0F2C59] px-4 py-3 text-base font-bold text-[#0F2C59]"
              >
                <Phone size={18} /> Appeler : 09 54 24 34 46
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
